/* eslint-disable no-underscore-dangle */
import * as React from 'react';
import { FormProvider, useForm, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { SessionAudienceTypes, SessionType } from 'config';
import {
  ClosableModal,
  ConfirmationModal,
  PrimaryButton,
} from 'components';
import { Box, Typography } from '@mui/material';
import SessionFeeForm from '../session-fees-form';
import sessionFeeFormValidation from '../../validation/session-fees-form-validation';
import { ISessionFeeForm } from '../../../../interface';
import {
  selectInitialLocations,
  selectLocations,
  selectPendingSlots,
  selectSessionFee,
  selectSessionInformation,
} from '../../../../selector';
import { createSessionActions } from '../../../../slice';
import './index.scss';

interface SessionFeeViewProps {
  editMode: boolean;
  onRequestOpenCancelSlotsModal: () => void;
}
export default function SessionFeeView({
  editMode,
  onRequestOpenCancelSlotsModal,
}: SessionFeeViewProps) {
  const dispatch = useDispatch();
  const sessionFee = useSelector(selectSessionFee);
  const { sessionType, audienceType, isObserverAllowed } = useSelector(selectSessionInformation);
  const locations = useSelector(selectLocations);
  const initialLocations = useSelector(selectInitialLocations);
  const pendingSlots = useSelector(selectPendingSlots);

  const [openMinimumFeeModal, setOpenMinimumFeeModal] = React.useState(false);
  const [openConfirmTimeChangeModal, setOpenConfirmTimeChangeModal] = React.useState(false);
  const [pendingSessionFee, setPendingSessionFee] = React.useState<ISessionFeeForm | null>(null);

  const handleOpenMinimumFeeModal = () => setOpenMinimumFeeModal(true);
  const handleCloseMinimumFeeModal = () => setOpenMinimumFeeModal(false);
  const handleCloseConfirmTimeChangeModal = () => {
    setOpenConfirmTimeChangeModal(false);
    setPendingSessionFee(null);
  };

  const methods = useForm<ISessionFeeForm>({
    defaultValues: {
      ...sessionFee,
    },
    resolver: yupResolver(sessionFeeFormValidation),
    context: { sessionType, audienceType, isObserverAllowed },
    shouldFocusError: true,
  });

  const currency = methods.watch('currency');

  const hasTimeChangesForEvent = React.useMemo(() => {
    if (!editMode || sessionType !== SessionType.event) {
      return false;
    }

    const getTimeStamp = (timeValue?: string) => (timeValue ? new Date(timeValue).getTime() : 0);
    return initialLocations.some((initialLocation, index: number) => {
      const currentLocation = locations[index];
      if (!currentLocation) {
        return true;
      }

      return (
        getTimeStamp(initialLocation.from) !== getTimeStamp(currentLocation.from)
        || getTimeStamp(initialLocation.to) !== getTimeStamp(currentLocation.to)
      );
    });
  }, [editMode, initialLocations, locations, sessionType]);

  function isMinimumChargeFeeApplied(session: ISessionFeeForm) {
    const minimumFee = 0.5;

    const getFee = (fee: number, discount: number) => fee - (fee * (discount / 100));

    let allFeesValid = true;

    if (audienceType === SessionAudienceTypes.subscription) {
      const regularFeeValid = Number(session.fee) === 0
        || getFee(Number(session.fee), Number(session.discount)) >= minimumFee;

      const subscriberFeeValid = Number(session.subscriberFee) === 0
        || getFee(Number(session.subscriberFee), Number(session.subscriberDiscount)) >= minimumFee;

      allFeesValid = regularFeeValid && subscriberFeeValid;
    } else {
      allFeesValid = Number(session.fee) === 0
        || getFee(Number(session.fee), Number(session.discount)) >= minimumFee;
    }

    // If observer is allowed, check observer fees
    if (isObserverAllowed && allFeesValid) {
      if (audienceType === SessionAudienceTypes.subscription) {
        // Check both regular observer fee and subscriber observer fee
        const regularObserverFeeValid = Number(session.observerFee) === 0
          || getFee(Number(session.observerFee), Number(session.observerDiscount)) >= minimumFee;

        const subscriberObserverFeeValid = Number(session.subscribeObserverFee) === 0
          // eslint-disable-next-line max-len
          || getFee(Number(session.subscribeObserverFee), Number(session.subscribeObserverDiscount)) >= minimumFee;

        allFeesValid = allFeesValid && regularObserverFeeValid && subscriberObserverFeeValid;
      } else {
        // Check regular observer fee for non-subscription
        allFeesValid = allFeesValid && (Number(session.observerFee) === 0
          || getFee(Number(session.observerFee), Number(session.observerDiscount)) >= minimumFee);
      }
    }

    return allFeesValid;
  }

  const onSubmit:
  SubmitHandler<ISessionFeeForm> = React.useCallback((data) => {
    if (!isMinimumChargeFeeApplied(data)) {
      handleOpenMinimumFeeModal();
      return;
    }

    if (!editMode) {
      dispatch(createSessionActions.createSession(data));
      return;
    }

    if (sessionType === SessionType.event && hasTimeChangesForEvent) {
      setPendingSessionFee(data);
      setOpenConfirmTimeChangeModal(true);
      return;
    }

    if (editMode) {
      dispatch(createSessionActions.editSession(data));
    }
  }, [dispatch, editMode, hasTimeChangesForEvent, sessionType]);

  const handleConfirmTimeChange = React.useCallback(() => {
    if (!pendingSessionFee) {
      return;
    }

    if (pendingSlots.length > 0) {
      onRequestOpenCancelSlotsModal();
    }

    dispatch(createSessionActions.editSession(pendingSessionFee));
    setOpenConfirmTimeChangeModal(false);
    setPendingSessionFee(null);
  }, [dispatch, onRequestOpenCancelSlotsModal, pendingSessionFee, pendingSlots.length]);

  return (
    <div>
      <FormProvider {...methods}>
        <ConfirmationModal
          open={openConfirmTimeChangeModal}
          onClose={handleCloseConfirmTimeChangeModal}
          title=""
          onSubmit={handleConfirmTimeChange}
          loading={false}
          success={false}
          closeHidden
          primaryButtonValue="Confirm time change"
          modalClass="session-slot-time-change-modal"
        >
          <Box sx={{ paddingBottom: '1.5rem', paddingTop: '1rem' }}>
            <Typography fontWeight="bold" sx={{ mb: 2, fontSize: '20px' }}>
              Changing the start date/time only affects future bookings.
              Existing bookings keep their original time slot.
            </Typography>
            <Typography sx={{ fontSize: '18px', fontWeight: 600, mb: 1 }}>
              Please note:
            </Typography>
            <ul style={{ marginBottom: 0 }}>
              <li>
                There will be two time slots: one for existing bookings and one for new bookings.
              </li>
              <li>Be aware of the potential impact on users who have already booked.</li>
            </ul>
          </Box>
        </ConfirmationModal>
        <ClosableModal
          title=""
          open={openMinimumFeeModal}
          onClose={handleCloseMinimumFeeModal}
          subTitle=""
        >
          <Typography variant="h3" textAlign="center">
            The minimum charge fee is 0.5
            {' '}
            {currency}
            . Please update the fee or discount to meet the minimum charge fee.
          </Typography>
          <div style={{ textAlign: 'center', marginTop: '1rem' }}>
            <PrimaryButton onClick={handleCloseMinimumFeeModal}>Ok</PrimaryButton>
          </div>
        </ClosableModal>
        <SessionFeeForm
          onSubmit={onSubmit}
          editMode={editMode}
        />
      </FormProvider>
    </div>
  );
}
